/**
 * AJAX Collection Filtering
 * Uses Shopify Section Rendering API for dynamic filtering
 */

// Debounce helper function
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Slide toggle helper functions
function slideDown(element, duration = 500) {
  element.style.removeProperty('display');
  element.style.display = 'block';
  const height = element.offsetHeight;
  element.style.overflow = 'hidden';
  element.style.height = 0;
  element.style.paddingTop = 0;
  element.style.paddingBottom = 0;
  element.style.marginTop = 0;
  element.style.marginBottom = 0;
  element.offsetHeight; // Force reflow
  element.style.boxSizing = 'border-box';
  element.style.transitionProperty = 'height, margin, padding';
  element.style.transitionDuration = duration + 'ms';
  element.style.height = height + 'px';
  element.style.removeProperty('padding-top');
  element.style.removeProperty('padding-bottom');
  element.style.removeProperty('margin-top');
  element.style.removeProperty('margin-bottom');
  
  window.setTimeout(() => {
    element.style.removeProperty('height');
    element.style.removeProperty('overflow');
    element.style.removeProperty('transition-duration');
    element.style.removeProperty('transition-property');
  }, duration);
}

function slideUp(element, duration = 500) {
  element.style.transitionProperty = 'height, margin, padding';
  element.style.transitionDuration = duration + 'ms';
  element.style.boxSizing = 'border-box';
  element.style.height = element.offsetHeight + 'px';
  element.offsetHeight; // Force reflow
  element.style.overflow = 'hidden';
  element.style.height = 0;
  element.style.paddingTop = 0;
  element.style.paddingBottom = 0;
  element.style.marginTop = 0;
  element.style.marginBottom = 0;
  
  window.setTimeout(() => {
    element.style.display = 'none';
    element.style.removeProperty('height');
    element.style.removeProperty('padding-top');
    element.style.removeProperty('padding-bottom');
    element.style.removeProperty('margin-top');
    element.style.removeProperty('margin-bottom');
    element.style.removeProperty('overflow');
    element.style.removeProperty('transition-duration');
    element.style.removeProperty('transition-property');
  }, duration);
}

function slideToggle(element, duration = 500) {
  if (window.getComputedStyle(element).display === 'none') {
    return slideDown(element, duration);
  } else {
    return slideUp(element, duration);
  }
}

class CollectionFiltersAjax {
  constructor() {
    this.selectors = {
      form: '[data-collection-filters-form]',
      inputs: '[data-collection-filters-form] input',
      sortLinks: '[data-sort-link]',
      productGrid: '#CollectionLoop',
      pagination: '.pagination',
      activeFilters: '.collection__active__filters',
      collectionNav: '[data-collection-nav]',
      section: '[data-section-type="collection"]',
      resultsCount: '.collection__sidebar__head h3'
    };

    this.currentRequest = null;
    this.init();
  }

  init() {
    this.form = document.querySelector(this.selectors.form);
    this.section = document.querySelector(this.selectors.section);
    
    if (!this.form || !this.section) return;
    
    this.sectionId = this.section.dataset.sectionId;
    this.bindEvents();
    this.preventFormSubmit();
  }

  preventFormSubmit() {
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
    });
  }

  bindEvents() {
    // Handle filter inputs
    const inputs = document.querySelectorAll(this.selectors.inputs);
    inputs.forEach(input => {
      // Use 'input' event for text/number inputs and 'change' for checkboxes
      const eventType = input.type === 'checkbox' ? 'change' : 'input';
      
      input.addEventListener(eventType, debounce(() => {
        // For price inputs, only trigger if value actually changed from default
        if (input.name && (input.name.includes('price.gte') || input.name.includes('price.lte'))) {
          const isMin = input.name.includes('price.gte');
          const defaultValue = isMin ? (input.getAttribute('min') || '0') : input.getAttribute('max');
          
          // If value matches default or placeholder, skip update
          if (input.value === defaultValue || input.value === input.placeholder || input.value === '') {
            return;
          }
        }
        
        this.updateCollection();
      }, 400));
    });

    // Handle range slider updates
    this.form.addEventListener('range:filter:update', () => {
      this.updateCollection();
    });

    // Handle sort links
    document.querySelectorAll(this.selectors.sortLinks).forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleSort(e.currentTarget);
      });
    });

    // Handle pagination links
    this.bindPaginationEvents();

    // Handle active filter removal
    this.bindActiveFilterEvents();
    
    // Initialize filter group collapse/expand
    this.initFilterGroupCollapse();
  }

  handleSort(link) {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);
    const sortValue = link.getAttribute('data-value');
    
    params.set('sort_by', sortValue);
    this.updateCollection(params);
  }

  saveFilterState() {
    const filterState = {};
    const headings = document.querySelectorAll('[data-collection-sidebar-heading]');
    
    headings.forEach((heading, index) => {
      filterState[index] = {
        isActive: heading.classList.contains('is-active'),
        label: heading.textContent.trim()
      };
    });
    
    return filterState;
  }

  restoreFilterState(savedState) {
    if (!savedState) return;
    
    const headings = document.querySelectorAll('[data-collection-sidebar-heading]');
    
    headings.forEach((heading, index) => {
      if (savedState[index]) {
        const filterList = heading.nextElementSibling;
        const showMore = filterList ? filterList.nextElementSibling : null;
        
        if (savedState[index].isActive) {
          heading.classList.add('is-active');
          if (filterList) filterList.style.display = 'block';
          if (showMore && showMore.hasAttribute('data-show-more')) {
            showMore.style.display = 'block';
          }
        } else {
          heading.classList.remove('is-active');
          if (filterList) filterList.style.display = 'none';
          if (showMore && showMore.hasAttribute('data-show-more')) {
            showMore.style.display = 'none';
          }
        }
      }
    });
  }

  updateCollection(customParams = null) {
    // Save current filter state before update
    const savedFilterState = this.saveFilterState();
    
    const formData = new FormData(this.form);
    let searchParams;
    
    if (customParams) {
      // Use custom params (for sort, pagination, filter removal)
      searchParams = new URLSearchParams(customParams);
    } else {
      // Build params from form data
      searchParams = new URLSearchParams();
      
      // Get all checked checkboxes directly (more reliable than FormData for checkboxes)
      const checkedInputs = this.form.querySelectorAll('input[type="checkbox"]:checked');
      checkedInputs.forEach(input => {
        searchParams.append(input.name, input.value);
      });
      
      // Handle price filters
      const priceInputs = this.form.querySelectorAll('input[type="number"]');
      priceInputs.forEach(input => {
        if (!input.value) return;
        
        const isMin = input.name.includes('price.gte');
        const isMax = input.name.includes('price.lte');
        
        if (isMin) {
          const defaultMin = input.getAttribute('min') || '0';
          if (input.value !== defaultMin && input.value !== input.placeholder) {
            searchParams.append(input.name, input.value);
          }
        } else if (isMax) {
          const defaultMax = input.getAttribute('max') || input.placeholder;
          if (input.value !== defaultMax && input.value !== input.placeholder) {
            searchParams.append(input.name, input.value);
          }
        } else {
          // Other number inputs
          searchParams.append(input.name, input.value);
        }
      });
      
      // Preserve existing URL params that aren't form fields (like sort_by)
      const currentParams = new URLSearchParams(window.location.search);
      for (const [key, value] of currentParams) {
        if (!key.startsWith('filter.') && !searchParams.has(key)) {
          searchParams.set(key, value);
        }
      }
    }
    
    // Build URL
    const url = `${window.location.pathname}?${searchParams.toString()}`;
    
    // Cancel any in-flight requests
    if (this.currentRequest) {
      this.currentRequest.abort();
    }
    
    // Create new AbortController for this request
    const abortController = new AbortController();
    this.currentRequest = abortController;
    
    // Show loading state
    this.setLoading(true);

    // Fetch updated section
    fetch(url, {
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      },
      signal: abortController.signal
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.text();
    })
    .then(html => {
      this.currentRequest = null;
      this.renderCollection(html, url, savedFilterState);
    })
    .catch(error => {
      if (error.name !== 'AbortError') {
        console.error('Error updating collection:', error);
      }
      this.currentRequest = null;
      this.setLoading(false);
    });
  }

  renderCollection(html, url, savedFilterState = null) {
    const parser = new DOMParser();
    const newDocument = parser.parseFromString(html, 'text/html');
    
    // Get section HTML using Section Rendering API format
    const newSection = newDocument.querySelector(this.selectors.section);
    
    if (!newSection) {
      // Fallback to updating specific elements
      this.updateElements(newDocument);
      
      // Reinitialize filter collapse after DOM update
      this.initFilterGroupCollapse(true);
      
      // Restore filter state for fallback method
      if (savedFilterState) {
        this.restoreFilterState(savedFilterState);
      }
    } else {
      // Update the entire section for Section Rendering API response
      const sectionElement = document.querySelector(`#shopify-section-${this.sectionId}`);
      if (sectionElement) {
        // Get the new section wrapper from the response
        const newSectionWrapper = newDocument.querySelector(`#shopify-section-${this.sectionId}`);
        
        if (newSectionWrapper) {
          // Replace the entire section element
          sectionElement.outerHTML = newSectionWrapper.outerHTML;
        } else {
          // Fallback to innerHTML if wrapper not found
          sectionElement.innerHTML = newSection.outerHTML;
        }
        
        // Reinitialize components
        this.reinitializeComponents();
        
        // Restore filter state after components are reinitialized
        if (savedFilterState) {
          this.restoreFilterState(savedFilterState);
        }
      }
    }
    
    // Update URL without reload
    window.history.pushState({}, '', url);
    
    // Hide loading state
    this.setLoading(false);
    
    // Only scroll for pagination, not for filter changes
    // This prevents unwanted jumping when filters are applied
    if (url.includes('page=') && !window.location.href.includes('page=')) {
      // This is pagination - scroll to top
      this.scrollToCollection();
    }
    
    // Trigger custom event
    document.dispatchEvent(new CustomEvent('collection:filters:updated'));
  }

  updateElements(newDocument) {
    // Update product grid
    const newProductGrid = newDocument.querySelector(this.selectors.productGrid);
    const currentProductGrid = document.querySelector(this.selectors.productGrid);
    if (newProductGrid && currentProductGrid) {
      currentProductGrid.innerHTML = newProductGrid.innerHTML;
    }
    
    // Update pagination
    const newPagination = newDocument.querySelector(this.selectors.pagination);
    const currentPagination = document.querySelector(this.selectors.pagination);
    if (newPagination && currentPagination) {
      currentPagination.innerHTML = newPagination.innerHTML;
    } else if (!newPagination && currentPagination) {
      currentPagination.remove();
    }
    
    // Update active filters
    const newActiveFilters = newDocument.querySelector(this.selectors.activeFilters);
    const currentActiveFilters = document.querySelector(this.selectors.activeFilters);
    if (newActiveFilters && currentActiveFilters) {
      currentActiveFilters.innerHTML = newActiveFilters.innerHTML;
    }
    
    // Update results count
    const newResultsCount = newDocument.querySelector(this.selectors.resultsCount);
    const currentResultsCount = document.querySelector(this.selectors.resultsCount);
    if (newResultsCount && currentResultsCount) {
      currentResultsCount.textContent = newResultsCount.textContent;
    }
  }

  reinitializeComponents() {
    // Reinitialize any components that need it
    
    // Remove existing event listeners to prevent duplicates
    if (this.abortController) {
      this.abortController.abort();
    }
    
    // Re-query elements after DOM update
    this.form = document.querySelector(this.selectors.form);
    this.section = document.querySelector(this.selectors.section);
    
    if (this.form && this.section) {
      this.sectionId = this.section.dataset.sectionId;
      
      // Create new abort controller for this set of event listeners
      this.abortController = new AbortController();
      const signal = this.abortController.signal;
      
      // Re-bind events with signal for proper cleanup
      this.bindEvents();
      this.preventFormSubmit();
    }
    
    // Reinitialize filter group collapse/expand (skip initial state since we'll restore it)
    this.initFilterGroupCollapse(true);
    
    // Reinitialize lazy loading
    if (window.lazySizes) {
      window.lazySizes.init();
    }
    
    // Reinitialize AOS animations
    if (window.AOS) {
      window.AOS.refresh();
    }
    
    // Trigger theme resize event for any components that listen to it
    document.dispatchEvent(new CustomEvent('theme:resize'));
    
    // Reinitialize product reviews if enabled
    if (window.SPR) {
      window.SPR.initDomEls();
      window.SPR.loadBadges();
    }
    
    // Reinitialize any product grid animations
    const products = document.querySelectorAll('.product-grid__item');
    products.forEach((product, index) => {
      product.classList.add('ajax-loaded');
      product.style.animationDelay = `${index * 50}ms`;
    });
  }

  setLoading(isLoading) {
    const productGrid = document.querySelector(this.selectors.productGrid);
    if (productGrid) {
      if (isLoading) {
        productGrid.style.opacity = '0.5';
        productGrid.style.pointerEvents = 'none';
      } else {
        productGrid.style.opacity = '1';
        productGrid.style.pointerEvents = 'auto';
      }
    }
  }

  scrollToCollection() {
    const collectionNav = document.querySelector(this.selectors.collectionNav);
    if (collectionNav) {
      const offset = collectionNav.getBoundingClientRect().top + window.pageYOffset - 100;
      window.scrollTo({
        top: offset,
        behavior: 'smooth'
      });
    }
  }

  bindPaginationEvents() {
    const paginationContainer = document.querySelector(this.selectors.pagination);
    if (!paginationContainer) return;

    paginationContainer.addEventListener('click', (e) => {
      const link = e.target.closest('a');
      if (link && link.href) {
        e.preventDefault();
        const url = new URL(link.href);
        const searchParams = new URLSearchParams(url.search);
        this.updateCollection(searchParams);
      }
    });
  }

  bindActiveFilterEvents() {
    // Use event delegation on a parent element that doesn't get replaced
    const container = document.querySelector('.collection__sidebar') || document.querySelector('.collection');
    if (!container) return;

    // Remove existing listener if any
    if (this.activeFilterHandler) {
      container.removeEventListener('click', this.activeFilterHandler);
    }

    // Create and store the handler
    this.activeFilterHandler = (e) => {
      // Handle remove filter links
      const removeLink = e.target.closest('a.active__filters__remove');
      if (removeLink && removeLink.href) {
        e.preventDefault();
        const url = new URL(removeLink.href);
        const searchParams = new URLSearchParams(url.search);
        this.updateCollection(searchParams);
        return;
      }

      // Handle clear all filters link
      const clearLink = e.target.closest('a.active__filters__clear');
      if (clearLink && clearLink.href) {
        e.preventDefault();
        const url = new URL(clearLink.href);
        const searchParams = new URLSearchParams(url.search);
        this.updateCollection(searchParams);
        return;
      }
    };

    // Add the listener
    container.addEventListener('click', this.activeFilterHandler);
  }

  initFilterGroupCollapse(skipInitialState = false) {
    const headings = document.querySelectorAll('[data-collection-sidebar-heading]');
    
    headings.forEach((heading, index) => {
      // Remove existing click handlers to prevent duplicates
      const newHeading = heading.cloneNode(true);
      heading.parentNode.replaceChild(newHeading, heading);
      
      // Get the filter list (next sibling)
      const filterList = newHeading.nextElementSibling;
      const showMore = filterList ? filterList.nextElementSibling : null;
      
      // Set initial state only if not skipping (i.e., first load)
      if (!skipInitialState) {
        if (index === 0) {
          newHeading.classList.add('is-active');
          if (filterList) filterList.style.display = 'block';
          if (showMore && showMore.hasAttribute('data-show-more')) {
            showMore.style.display = 'block';
          }
        } else {
          newHeading.classList.remove('is-active');
          if (filterList) filterList.style.display = 'none';
          if (showMore && showMore.hasAttribute('data-show-more')) {
            showMore.style.display = 'none';
          }
        }
      }
      
      // Add click handler
      newHeading.addEventListener('click', (e) => {
        e.preventDefault();
        
        // Toggle active class
        newHeading.classList.toggle('is-active');
        
        // Toggle filter list
        if (filterList) {
          slideToggle(filterList, 300);
        }
        
        // Toggle show more link if present
        if (showMore && showMore.hasAttribute('data-show-more')) {
          slideToggle(showMore, 300);
        }
      });
    });
  }

  destroy() {
    // Cancel any pending requests
    if (this.currentRequest) {
      this.currentRequest.abort();
    }
    
    // Remove event listeners
    if (this.abortController) {
      this.abortController.abort();
    }
    
    // Remove active filter handler
    if (this.activeFilterHandler) {
      const container = document.querySelector('.collection__sidebar') || document.querySelector('.collection');
      if (container) {
        container.removeEventListener('click', this.activeFilterHandler);
      }
    }
    
    // Clear references
    this.form = null;
    this.section = null;
    this.activeFilterHandler = null;
  }
}

// Store instance globally to prevent duplicates
window.collectionFiltersAjax = null;

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  if (!window.collectionFiltersAjax) {
    window.collectionFiltersAjax = new CollectionFiltersAjax();
  }
});

// Reinitialize on section load (Theme Editor)
document.addEventListener('shopify:section:load', (event) => {
  if (event.target.querySelector('[data-section-type="collection"]')) {
    // Destroy existing instance
    if (window.collectionFiltersAjax && window.collectionFiltersAjax.destroy) {
      window.collectionFiltersAjax.destroy();
    }
    window.collectionFiltersAjax = new CollectionFiltersAjax();
  }
});